import { useContainerRelativePosition } from '../hooks/useContainerRelativePosition'
import {
  PopoverElementKind,
  useHoverCardPosition,
} from '../hooks/useHoverCardPosition'
import { useLightMode } from '@/hooks/useLightMode'

import { Box, Popover, PopoverArrow, PopoverContent } from '@chakra-ui/react'

import { DarkProvider } from '@hivewatch/dark'

import { PopoverTrigger } from '@/components/ui'

export interface DarkFloorPlanPopoverIProps {
  children: React.ReactNode
  elementId: string
  kind?: PopoverElementKind
  isHovering: boolean
  forceCloseSignal?: number
  forceOpenSignal?: number
}

/**
 * Card width per anchor kind. The tailgate card is narrower because it holds
 * two device rows and copy, while the device card has to fit a camera feed.
 */
const POPOVER_WIDTH: Record<PopoverElementKind, string> = {
  device: '400px',
  tailgate: '320px',
}

/**
 * Dark twin of FloorPlanPopover. The anchoring — the open delay, the one-shot
 * client rect and the shared useContainerRelativePosition mapping — is kept
 * identical to the legacy component so this card appears exactly where the
 * legacy one does. Only the surface is rebranded.
 */
export const DarkFloorPlanPopover = ({
  isHovering = false,
  elementId,
  children,
  kind,
  forceCloseSignal,
  forceOpenSignal,
}: DarkFloorPlanPopoverIProps) => {
  const colorScheme = useLightMode()

  const {
    triggerPosition,
    isOpen,
    onContentMouseEnter,
    onContentMouseLeave,
    onPopoverClose,
  } = useHoverCardPosition({
    elementId,
    isHovering,
    kind,
    forceCloseSignal,
    forceOpenSignal,
  })

  const { width, height } = triggerPosition
  const { anchorRef, left, top } = useContainerRelativePosition(triggerPosition)
  return (
    <Popover
      autoFocus={false}
      closeOnBlur={true}
      isLazy
      isOpen={isOpen}
      offset={[8, 8]}
      onClose={onPopoverClose}
      returnFocusOnClose={false}
      // Not 'hover': Chakra's own hover trigger schedules an unconditional
      // close on mouseleave that can't be cancelled, which used to kill a
      // card re-opened right after. Open/close is fully driven by the hook.
      // autoFocus/returnFocusOnClose default true under Chakra's default
      // 'click' trigger, which would steal focus from e.g. a search input
      // whenever the pointer happens to rest on a marker long enough to
      // open the card.
    >
      <PopoverTrigger>
        {/* Anchor only, for Popper to position against — pointerEvents='none'
            so a stale rect can never intercept a click on the canvas. left/top
            are the marker rect mapped into the anchor's positioned ancestor by
            useContainerRelativePosition, so the card tracks its marker at any
            side-nav width. */}
        <Box
          height={height}
          left={left}
          pointerEvents='none'
          pos='absolute'
          ref={anchorRef}
          top={top}
          width={width}
        />
      </PopoverTrigger>
      {/* The surface is Chakra v1 and stays outside DarkProvider so its tokens
          resolve against the v1 theme; DarkProvider wraps only the content.
          Square corners and the subtle border mirror the dark package's Card,
          which sets no radius — radius/none is the design library's default for
          every standard product surface, and the chamfer is modal-only. */}
      <PopoverContent
        bg={`bg.card-elevated.${colorScheme}`}
        border='1px solid'
        borderColor={`border.subtle.${colorScheme}`}
        borderRadius='none'
        onMouseEnter={onContentMouseEnter}
        onMouseLeave={onContentMouseLeave}
        shadow='xl'
        width={POPOVER_WIDTH[kind ?? 'device']}
      >
        <PopoverArrow bg={`bg.card-elevated.${colorScheme}`} />
        <DarkProvider colorScheme={colorScheme}>{children}</DarkProvider>
      </PopoverContent>
    </Popover>
  )
}
